import React, { memo, useCallback, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import useNowTick from 'hooks/useNowTick';

import { AiActivityTestIds } from '../../../AiActivity.testIds';

import {
  TIMELINE_TITLE,
  TIMELINE_TOGGLE_LABELS,
  WAITING_BAR_SUFFIX,
} from './RunTimeline.constants';
import {
  getBarStyles,
  getNowMarkerStyles,
  headerRowStyles,
  labelCellStyles,
  labelsColumnStyles,
  summaryStyles,
  timelineStyles,
  trackAreaStyles,
  trackStyles,
  tracksStyles,
} from './RunTimeline.styles';
import { buildTimeline, formatTimelineSummary } from './RunTimeline.utils';

const RunTimeline = ({ steps, runStartedAt, runCompletedAt }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLive = !runCompletedAt;

  useNowTick(isLive);

  const onToggle = useCallback(() => setIsExpanded(value => !value), []);

  const timeline = buildTimeline({ steps, runStartedAt, runCompletedAt });

  if (timeline === null) {
    return null;
  }

  return (
    <Box data-testid={AiActivityTestIds.RUN_TIMELINE} sx={timelineStyles}>
      <Box sx={headerRowStyles}>
        <Typography variant="bold14">{TIMELINE_TITLE}</Typography>
        <Typography
          data-testid={AiActivityTestIds.RUN_TIMELINE_SUMMARY}
          sx={summaryStyles}
          variant="regular12"
        >
          {formatTimelineSummary(timeline)}
        </Typography>
        <Button
          aria-expanded={isExpanded}
          data-testid={AiActivityTestIds.RUN_TIMELINE_TOGGLE}
          size="small"
          variant="text"
          onClick={onToggle}
        >
          {isExpanded ? TIMELINE_TOGGLE_LABELS.HIDE : TIMELINE_TOGGLE_LABELS.SHOW}
        </Button>
      </Box>
      {isExpanded && (
        <Box sx={trackAreaStyles}>
          <Box sx={labelsColumnStyles}>
            {timeline.rows.map(row => (
              <Typography component="div" key={row.id} sx={labelCellStyles} variant="regular12">
                {row.name}
              </Typography>
            ))}
          </Box>
          <Box sx={tracksStyles}>
            {timeline.rows.map(row => (
              <Box key={row.id} sx={trackStyles}>
                {row.widthPercent > 0 && (
                  <Box
                    data-testid={AiActivityTestIds.RUN_TIMELINE_BAR}
                    sx={getBarStyles(row)}
                    title={`${row.name} · ${row.status}${row.isOpenEnded ? WAITING_BAR_SUFFIX : ''}`}
                  />
                )}
              </Box>
            ))}
            {timeline.nowPercent !== null && <Box sx={getNowMarkerStyles(timeline.nowPercent)} />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

RunTimeline.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  runCompletedAt: PropTypes.string,
  runStartedAt: PropTypes.string,
};

export default memo(RunTimeline);
